import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  BrowserAuthError,
} from "@azure/msal-browser";

// ─── Config ───────────────────────────────────────────────────────────────────

const CLIENT_ID = "714e6a98-fba3-476d-84cb-5d37b4965389"; // 🔑 Replace with your Azure App client ID

export const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: "https://login.microsoftonline.com/common",
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

export const tokenRequest = {
  scopes: [
    "Files.ReadWrite",
    "Mail.Read",
    "Sites.ReadWrite.All",
    "User.Read",
    "offline_access" 
  ],
};

// ─── MSAL Instance (module-level singleton) ───────────────────────────────────

let msalInstance: IPublicClientApplication | undefined = undefined;

// ─── Error Types ──────────────────────────────────────────────────────────────

export type TokenResult =
  | { success: true; accessToken: string }
  | { success: false; error: TokenError };

export type IdTokenResult =
  | { success: true; idToken: string }
  | { success: false; error: TokenError };


export type AuthCodeResult =
  | { success: true; code: string }
  | { success: false; error: TokenError };

export type TokenError =
  | "ADMIN_CONSENT_REQUIRED"   // tenant admin needs to grant permissions
  | "POPUP_BLOCKED"            // browser blocked the popup
  | "USER_CANCELLED"           // user closed the popup/login
  | "NETWORK_ERROR"            // fetch/network issue
  | "UNKNOWN_ERROR";           // anything else

// ─── Helpers ─────────────────────────────────────────────────────────────────

function classifyError(error: unknown): TokenError {
  const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  if (
    message.includes("aadsts90094") ||  // admin consent required
    message.includes("aadsts65001") ||  // user or admin has not consented
    message.includes("aadsts700082") || // refresh token expired due to consent change
    message.includes("consent_required")
  ) {
    return "ADMIN_CONSENT_REQUIRED";
  }

  if (
    message.includes("popup_window_error") ||
    message.includes("popupwindow") ||
    message.includes("popup blocked")
  ) {
    return "POPUP_BLOCKED";
  }

  if (
    message.includes("user_cancelled") ||
    message.includes("user cancelled") ||
    message.includes("access_denied")
  ) {
    return "USER_CANCELLED";
  }

  if (
    message.includes("network") ||
    message.includes("failed to fetch")
  ) {
    return "NETWORK_ERROR";
  }

  return "UNKNOWN_ERROR";
}

// ─── Public Functions ─────────────────────────────────────────────────────────

/**
 * Initialize MSAL as a nestable public client application.
 * Reuses the existing instance if already initialized (singleton).
 */
export async function initMsal(): Promise<IPublicClientApplication> {
  if (!msalInstance) {
    msalInstance = await createNestablePublicClientApplication(msalConfig);
  }
  return msalInstance;
}

/**
 * Acquires an access token.
 * - Tries silent acquisition first (no UI).
 * - Falls back to popup if interaction is required (MFA, consent, etc).
 * - Handles admin consent errors explicitly.
 *
 * @returns TokenResult — either { success: true, accessToken } or { success: false, error }
 *
 * @example
 * const result = await getAccessToken();
 * if (result.success) {
 *   const token = result.accessToken;
 * } else {
 *   if (result.error === "ADMIN_CONSENT_REQUIRED") { ... }
 * }
 */
export async function getAccessToken(): Promise<TokenResult> {
  try {
    const instance = await initMsal();

    // ── Try silent first ──────────────────────────────────────────────────────
    try {
      const userAccount = await instance.acquireTokenSilent(tokenRequest);
      console.log("Acquired token silently.");
      return { success: true, accessToken: userAccount.accessToken };
    } catch (silentError) {
      // Silent failed — check if interaction can fix it
      if (!(silentError instanceof InteractionRequiredAuthError)) {
        // Non-recoverable silent error (e.g. admin consent, network)
        console.error("Silent token acquisition failed (non-recoverable):", silentError);
        return { success: false, error: classifyError(silentError) };
      }

      console.log("Silent acquisition requires interaction, trying popup...");
    }

    // ── Fallback to popup ─────────────────────────────────────────────────────
    try {
      const instance = await initMsal();
      const userAccount = await instance.acquireTokenPopup(tokenRequest);
      console.log("Acquired token via popup.");
      return { success: true, accessToken: userAccount.accessToken };
    } catch (popupError) {
      console.error("Popup token acquisition failed:", popupError);
      return { success: false, error: classifyError(popupError) };
    }

  } catch (unexpectedError) {
    console.error("Unexpected error during token acquisition:", unexpectedError);
    return { success: false, error: classifyError(unexpectedError) };
  }
}

/**
 * Acquires an ID token for backend routes that validate `aud = AZURE_CLIENT_ID`.
 * Use this for API routes protected by auth.middleware.ts, not for Microsoft Graph proxy routes.
 */
export async function getIdToken(): Promise<IdTokenResult> {
  try {
    const instance = await initMsal();

    try {
      const result = await instance.acquireTokenSilent(tokenRequest);
      if (!result.idToken) throw new Error("MSAL response did not include an ID token.");
      return { success: true, idToken: result.idToken };
    } catch (silentError) {
      if (!(silentError instanceof InteractionRequiredAuthError)) {
        console.error("Silent ID token acquisition failed (non-recoverable):", silentError);
        return { success: false, error: classifyError(silentError) };
      }
    }

    try {
      const result = await instance.acquireTokenPopup(tokenRequest);
      if (!result.idToken) throw new Error("MSAL response did not include an ID token.");
      return { success: true, idToken: result.idToken };
    } catch (popupError) {
      console.error("Popup ID token acquisition failed:", popupError);
      return { success: false, error: classifyError(popupError) };
    }
  } catch (unexpectedError) {
    console.error("Unexpected error during ID token acquisition:", unexpectedError);
    return { success: false, error: classifyError(unexpectedError) };
  }
}


/**
 * Gets an authorization CODE (not token) via popup.
 * Send this code to your backend — backend exchanges it for
 * access token + refresh token and stores them for cron jobs.
 *
 * The backend must use client secret + this code to get tokens via:
 * POST https://login.microsoftonline.com/common/oauth2/v2.0/token
 */
export async function getAuthCode(): Promise<AuthCodeResult> {
  try {
    const instance = await initMsal();

    const result = await instance.acquireTokenPopup({
      ...tokenRequest,
      responseType: "code", // request auth code, not token
    } as any);

    // MSAL returns the code in the response
    const code = (result as any).code;

    if (!code) {
      console.error("No auth code in response:", result);
      return { success: false, error: "UNKNOWN_ERROR" };
    }

    console.log("Got auth code for backend exchange.");
    return { success: true, code };

  } catch (error) {
    console.error("Auth code acquisition failed:", error);
    return { success: false, error: classifyError(error) };
  }
}
